import { useRouter } from 'next/router';
import { useTranslation } from '../../../lib/i18n';
import { useBasePath } from '../../../lib/use-base-path';
import { useGenericBi } from '../../../lib/use-generic-bi';
import { useWaGroupRoster } from '../../../lib/use-wa-group-roster';
import { waGroupHeaderSubtitle, waGroupScreenPath } from '../../../lib/wa-group-roster';
import { Button } from '../ui';
import { WhatsAppGlyph } from './WhatsAppGlyph';

/**
 * A WhatsApp-group thread's chat header: the WhatsApp glyph and the group's name over "WhatsApp group · Arava · invited,
 * Dana · joined" (or "… · nobody invited yet"), read from the chat list's roster. The whole header opens the group's own
 * screen (`whatsapp/group/{id}`), the way back to its settings that was missing. Same shape as `GroupChatHeader`, and
 * mirrors iOS / Android's WhatsApp-group header.
 */
export function WaGroupChatHeader({ title, conversationId, waGroupId: rowWaGroupId }: { title: string; conversationId: string; waGroupId?: string }) {
  const { t } = useTranslation();
  const router = useRouter();
  const basePath = useBasePath();
  const { logPageClickBi } = useGenericBi();
  const roster = useWaGroupRoster(conversationId, true);
  const waGroupId = roster?.waGroupId ?? rowWaGroupId ?? null;
  const subtitle = waGroupHeaderSubtitle(roster, t);
  return (
    <Button
      variant="text"
      size="small"
      data-testid="wa-group-chat-header"
      disabled={!waGroupId}
      onClick={() => {
        if (!waGroupId) return;
        logPageClickBi({ page_name: 'chat', button_name: 'wa_group_header_open', conversation_id: conversationId });
        void router.push(`${basePath}${waGroupScreenPath(waGroupId)}`);
      }}
      style={{ minWidth: 0, maxWidth: '100%', textAlign: 'start' }}
    >
      <span style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
        <span aria-hidden style={{ display: 'inline-flex', flexShrink: 0, color: 'var(--ds-text-secondary)' }}><WhatsAppGlyph size={16} /></span>
        <span style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          <span style={{ fontSize: 14, lineHeight: '18px', fontWeight: 'var(--sym-weight-semibold)', color: 'var(--ds-text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {title}
          </span>
          <span data-testid="wa-group-chat-header-subtitle" style={{ fontSize: 12, lineHeight: '16px', color: 'var(--ds-text-secondary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {subtitle}
          </span>
        </span>
      </span>
    </Button>
  );
}
